import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Instagram, Twitter, Youtube, Send, Music2, MessageCircle } from "lucide-react";
import api from "../lib/api";

function TabButton({ active, onClick, children, tid }) {
  return (
    <button
      data-testid={tid}
      onClick={onClick}
      className={`flex-1 px-4 py-3 text-sm rounded-lg border transition ${
        active ? "bg-[#7A2E2A] border-[#F0E8DB] text-[#F0E8DB]" : "border-[#F0E8DB]/25 text-[#C5B8A8]"
      }`}
    >
      {children}
    </button>
  );
}

export default function ContactSection({ settings, content }) {
  const committees = [...content.committee_list.map((c) => c.name), "غير محدد"];
  const [tab, setTab] = useState("join");
  const [loading, setLoading] = useState(false);
  const [joinForm, setJoinForm] = useState({ name: "", email: "", phone: "", committee: committees[0], message: "" });
  const [contactForm, setContactForm] = useState({ name: "", email: "", subject: "", message: "" });

  const submit = async (type) => {
    setLoading(true);
    try {
      const payload = type === "join"
        ? { ...joinForm, type: "join", subject: `طلب انضمام - ${joinForm.committee}` }
        : { ...contactForm, type: "contact" };
      await api.post("/messages", payload);
      toast.success(type === "join" ? "تم إرسال طلب الانضمام. سنتواصل معك قريباً." : "تم إرسال رسالتك، شكراً لتواصلك.");
      if (type === "join") setJoinForm({ name: "", email: "", phone: "", committee: committees[0], message: "" });
      else setContactForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      toast.error("حدث خطأ. حاول مرة أخرى.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full input-theater rounded-lg px-4 py-3 text-sm placeholder-[#BFB3A0]";

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-28 bg-[#1A0F0E]">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-12">
          <div className="text-[#F0E8DB] text-xs tracking-[0.4em] font-serif-en mb-3">JOIN · CONTACT</div>
          <h2 className="section-title text-3xl sm:text-4xl">{content.titles.contact}</h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 theater-card rounded-2xl p-6 sm:p-8">
            <div className="flex gap-2 mb-6">
              <TabButton active={tab === "join"} onClick={() => setTab("join")} tid="tab-join">طلب انضمام</TabButton>
              <TabButton active={tab === "contact"} onClick={() => setTab("contact")} tid="tab-contact">تواصل عام</TabButton>
            </div>

            {tab === "join" ? (
              <div className="space-y-4">
                <input data-testid="join-name" className={inputCls} placeholder="الاسم الكامل" value={joinForm.name} onChange={(e) => setJoinForm({ ...joinForm, name: e.target.value })} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="join-email" type="email" className={inputCls} placeholder="البريد الإلكتروني" value={joinForm.email} onChange={(e) => setJoinForm({ ...joinForm, email: e.target.value })} />
                  <input data-testid="join-phone" className={inputCls} placeholder="رقم الجوال" value={joinForm.phone} onChange={(e) => setJoinForm({ ...joinForm, phone: e.target.value })} />
                </div>
                <select data-testid="join-form-committee-select" className={inputCls} value={joinForm.committee} onChange={(e) => setJoinForm({ ...joinForm, committee: e.target.value })}>
                  {committees.map((c) => <option key={c} value={c} className="bg-[#241615]">{c}</option>)}
                </select>
                <textarea data-testid="join-message" className={`${inputCls} min-h-[120px]`} placeholder="عرّف بنفسك ومواهبك" value={joinForm.message} onChange={(e) => setJoinForm({ ...joinForm, message: e.target.value })} />
                <button
                  data-testid="join-form-submit-button"
                  disabled={loading || !joinForm.name || !joinForm.email || !joinForm.message}
                  onClick={() => submit("join")}
                  className="btn-gold rounded-full px-6 py-3 w-full sm:w-auto inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <Send size={16} /> إرسال طلب الانضمام
                </button>
                {settings?.external_join_form_url && (
                  <p className="text-xs text-[#BFB3A0] mt-2">
                    أو استخدم <a href={settings.external_join_form_url} target="_blank" rel="noreferrer" className="text-[#F0E8DB] underline">فورم Google الرسمي</a>.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <input data-testid="contact-name" className={inputCls} placeholder="الاسم" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="contact-email" type="email" className={inputCls} placeholder="البريد الإلكتروني" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
                  <input data-testid="contact-subject" className={inputCls} placeholder="الموضوع" value={contactForm.subject} onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })} />
                </div>
                <textarea data-testid="contact-message" className={`${inputCls} min-h-[140px]`} placeholder="رسالتك" value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} />
                <button
                  data-testid="contact-form-submit-button"
                  disabled={loading || !contactForm.name || !contactForm.email || !contactForm.message}
                  onClick={() => submit("contact")}
                  className="btn-primary rounded-full px-6 py-3 w-full sm:w-auto inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <Send size={16} /> إرسال الرسالة
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="theater-card rounded-2xl p-6">
              <h3 className="text-[#F0E8DB] font-semibold text-lg mb-4">قنوات التواصل</h3>
              <div className="space-y-3 text-sm">
                {settings?.email && (
                  <a href={`mailto:${settings.email}`} className="flex items-center gap-3 text-[#C5B8A8] hover:text-[#F0E8DB]">
                    <Mail size={16} className="text-[#F0E8DB]" /> {settings.email}
                  </a>
                )}
                {settings?.instagram && (
                  <a href={settings.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[#C5B8A8] hover:text-[#F0E8DB]">
                    <Instagram size={16} className="text-[#F0E8DB]" /> Instagram
                  </a>
                )}
                {settings?.twitter && (
                  <a href={settings.twitter} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[#C5B8A8] hover:text-[#F0E8DB]">
                    <Twitter size={16} className="text-[#F0E8DB]" /> X / Twitter
                  </a>
                )}
                {settings?.tiktok && (
                  <a data-testid="contact-tiktok-link" href={settings.tiktok} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[#C5B8A8] hover:text-[#F0E8DB]">
                    <Music2 size={16} className="text-[#F0E8DB]" /> TikTok
                  </a>
                )}
                {settings?.whatsapp && (
                  <a data-testid="contact-whatsapp-link" href={settings.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[#C5B8A8] hover:text-[#F0E8DB]">
                    <MessageCircle size={16} className="text-[#F0E8DB]" /> مجموعة WhatsApp
                  </a>
                )}
                {settings?.youtube && (
                  <a href={settings.youtube} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[#C5B8A8] hover:text-[#F0E8DB]">
                    <Youtube size={16} className="text-[#F0E8DB]" /> YouTube
                  </a>
                )}
              </div>
            </div>
            <div className="theater-card rounded-2xl p-6">
              <div className="text-[#F0E8DB] font-serif-en text-sm tracking-widest mb-2">STAGE CALL</div>
              <p className="text-[#C5B8A8] text-sm leading-relaxed">
                هل الخشبة تناديك؟ لا تُكتفِ بالمشاهدة — ارفع الستار على موهبتك وانضم إلى رحلة النادي.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
